import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, XCircle, Loader2, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResourcePaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, getAuthHeader } = useAuth();
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('Verifying your payment...');
  const [resourceId, setResourceId] = useState(null);
  const [resource, setResource] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && user) {
      checkPaymentStatus();
    } else if (!sessionId) {
      setStatus('error');
      setMessage('Invalid payment session');
    }
  }, [sessionId, user]);

  const checkPaymentStatus = async () => {
    if (pollCount >= 10) {
      setStatus('timeout');
      setMessage('Payment verification timed out. Please check your purchases.');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/payments/resource/status/${sessionId}`,
        getAuthHeader()
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage('Payment successful! Resource unlocked.');
        setResourceId(response.data.resource_id);
        toast.success('🎉 Resource purchased successfully!');
        
        // Fetch resource details for download
        if (response.data.resource_id) {
          try {
            const resourcesRes = await axios.get(`${API}/resources`, getAuthHeader());
            const foundResource = resourcesRes.data.find(r => r.id === response.data.resource_id);
            setResource(foundResource);
          } catch (e) {
            console.error('Failed to fetch resource:', e);
          }
        }
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        setMessage('Payment session expired. Please try again.');
      } else {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Error verifying payment');
    }
  };

  const handleDownload = () => {
    if (resource?.file_url) {
      window.open(resource.file_url, '_blank');
      toast.success(`Downloading ${resource.title}...`);
    } else {
      toast.error('Download link not available');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        {status === 'checking' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-indigo-100 rounded-full flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Processing Payment</h1>
            <p className="text-slate-600 mb-6">{message}</p>
            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-indigo-600 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(pollCount * 10, 90)}%` }}
              ></div>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Purchase Successful!</h1>
            <p className="text-slate-600 mb-6">{message}</p>
            
            {resource && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-teal-600" />
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="font-semibold text-slate-900">{resource.title}</h3>
                    <p className="text-sm text-slate-500">{resource.type}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              {resource?.file_url && (
                <Button
                  data-testid="download-resource-btn"
                  onClick={handleDownload}
                  className="w-full rounded-full bg-teal-600 hover:bg-teal-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Now
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate('/student/resources')}
                className="w-full rounded-full"
              >
                View All Resources
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/student/dashboard')}
                className="w-full rounded-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Session Expired</h1>
            <p className="text-slate-600 mb-6">{message}</p>
            <Button
              onClick={() => navigate('/student/resources')}
              className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
            >
              Browse Resources
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Error</h1>
            <p className="text-slate-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/student/resources')}
                className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                Try Again
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/student/dashboard')}
                className="w-full rounded-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Verification Timeout</h1>
            <p className="text-slate-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Button
                onClick={() => {
                  setPollCount(0);
                  setStatus('checking');
                  checkPaymentStatus();
                }}
                className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                Retry Verification
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/student/resources')}
                className="w-full rounded-full"
              >
                View My Resources
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
